import { ampli, type PortfolioDataRefreshedProperties } from '@/ampli';
import type { ExpandedState, SortingState } from '@tanstack/react-table';
import { getCoreRowModel, getExpandedRowModel, useReactTable } from '@tanstack/react-table';
import { usePrevious } from '@uidotdev/usehooks';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { HeaderConnectButton } from '@/components/Header/HeaderButtons';
import { FeedbackModal } from '@/components/Modal/FeedBackFormModal';

import { CHAIN_IDS, NETWORK_NAME_PER_CHAIN_ID } from '@/constants/chain';

import { useFiatDisplay } from '@/hooks/useFiatDisplay';
import { useMatchaWallets } from '@/hooks/useMatchaWallets';
import { useTokensPnL } from '@/hooks/useTokensPnL';
import { useWalletChart } from '@/hooks/useWalletChart';
import { useWalletPnL } from '@/hooks/useWalletPnL';
import { useWalletPortfolio } from '@/hooks/useWalletPortfolio';
import { useWalletPositions } from '@/hooks/useWalletPositions';

import { fontStyles } from '@/styles/typography';

import { classNames } from '@/utils/classnames';
import type {
  GroupedTokenBalance,
  SortColumn,
  SortDirection,
  TokenBalance,
  TokenPnL,
} from '@/utils/zerion/types';

import { portfolioColumns } from './columns';
import { PortfolioHeader } from './PortfolioHeader';
import { PortfolioTable } from './PortfolioTable';
import { PortfolioTriggerOrdersModal } from './PortfolioTriggerOrdersModal';
import type {
  ChainFilterOption,
  PortfolioRow,
  PortfolioTableMeta,
  TriggerOrderTarget,
} from './types';
import type { WalletFilter, WalletFilterOption } from './types';
import { usePortfolioFeedbackPrompt } from './usePortfolioFeedbackPrompt';
import {
  buildPnLTokenBlocks,
  buildPortfolioRows,
  groupTokensByFungible,
  isGroupedToken,
  isSortColumn,
} from './utils';

const containerClass = classNames(
  'bg-special-background-card',
  'rounded-[20px]',
  'desktop:max-w-[1240px]',
  'w-full',
  'min-w-0',
  // Match Homepage DiscoveryTable: horizontal scroll inside the card instead of clipping
  'overflow-x-auto',
  'no-scrollbar',
  'mx-auto',
);

interface PortfolioProps {
  /** Optional EVM address to view - if not provided, uses connected wallet */
  evmAddress?: string;
  /** Optional Solana address to view - if not provided, uses connected wallet */
  solanaAddress?: string;
}

export function Portfolio({
  evmAddress: propsEvmAddress,
  solanaAddress: propsSolanaAddress,
}: PortfolioProps = {}) {
  const { ethereumWallet, solanaWallet, isConnected } = useMatchaWallets();
  const { formatFiat } = useFiatDisplay();
  const [selectedChain, setSelectedChain] = useState<number | 'all'>('all');
  const [selectedWallet, setSelectedWallet] = useState<WalletFilter>('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [showSmallBalances, setShowSmallBalances] = useState(false);
  const [showUnpricedTokens, setShowUnpricedTokens] = useState(false);
  const [triggerOrderTarget, setTriggerOrderTarget] = useState<TriggerOrderTarget | null>(null);
  const [chartPeriod, setChartPeriod] = useState<
    'hour' | 'day' | 'week' | 'month' | 'year' | 'max'
  >('week');

  const [sortColumn, setSortColumn] = useState<SortColumn>('value');
  const [sortDirection, setSortDirection] = useState<SortDirection>('desc');

  const [expanded, setExpanded] = useState<ExpandedState>({});

  // Public portfolios (e.g. /portfolio/<address>) must show only the address in
  // the URL — never the viewer's connected wallets — so don't fall back when
  // viewing someone else's portfolio.
  const isViewingOtherWallet = Boolean(propsEvmAddress || propsSolanaAddress);
  const evmAddress = isViewingOtherWallet ? propsEvmAddress : ethereumWallet?.address;
  const solAddress = isViewingOtherWallet ? propsSolanaAddress : solanaWallet?.address;

  const hasWalletToView = Boolean(evmAddress || solAddress);
  const enabled = isViewingOtherWallet ? hasWalletToView : isConnected;

  const walletOptions = useMemo((): WalletFilterOption[] => {
    // Public portfolio page: show only the viewed address with a chain icon
    if (isViewingOtherWallet) {
      if (propsEvmAddress) {
        return [{ id: 'evm', label: 'EVM', address: propsEvmAddress, chainId: CHAIN_IDS.MAINNET }];
      }
      if (propsSolanaAddress) {
        return [
          { id: 'solana', label: 'Solana', address: propsSolanaAddress, chainId: CHAIN_IDS.SOLANA },
        ];
      }
      return [];
    }

    const options: WalletFilterOption[] = [];

    if (evmAddress && solAddress) {
      options.push({ id: 'all', label: 'All Wallets' });
    }

    if (evmAddress && ethereumWallet) {
      options.push({ id: 'evm', label: 'EVM', address: evmAddress, wallet: ethereumWallet });
    }

    if (solAddress && solanaWallet) {
      options.push({ id: 'solana', label: 'Solana', address: solAddress, wallet: solanaWallet });
    }

    return options;
  }, [
    isViewingOtherWallet,
    propsEvmAddress,
    propsSolanaAddress,
    evmAddress,
    solAddress,
    ethereumWallet,
    solanaWallet,
  ]);

  useEffect(() => {
    if (isViewingOtherWallet && walletOptions.length === 1) {
      setSelectedWallet(walletOptions[0].id);
    }
  }, [isViewingOtherWallet, walletOptions]);

  const effectiveEvmAddress = selectedWallet === 'solana' ? undefined : evmAddress;
  const effectiveSolAddress = selectedWallet === 'evm' ? undefined : solAddress;

  const { allTokens, totalValueUsd, isLoading, isRefetching, error, refetch } = useWalletPositions({
    evmAddress: effectiveEvmAddress,
    solanaAddress: effectiveSolAddress,
    enabled,
  });

  // Set when the user clicks refresh or retry; consumed once on the next fire
  // so the trigger can be stamped as `user_initiated`.
  const userInitiatedRefreshRef = useRef(false);
  const handleRefresh = useCallback(() => {
    userInitiatedRefreshRef.current = true;
    refetch();
  }, [refetch]);

  // Reflects the addresses actually fetched by `useWalletPositions` so the
  // event matches the refreshed dataset (e.g., 1 when the wallet filter is
  // narrowed to a single chain even though both wallets are connected).
  const walletCount = (effectiveEvmAddress ? 1 : 0) + (effectiveSolAddress ? 1 : 0);
  const chainCount = useMemo(
    () => new Set(allTokens.map((token) => token.chainId)).size,
    [allTokens],
  );

  // Fires `portfolio_data_refreshed` on the trailing edge of each completed
  // wallet positions fetch for the connected portfolio view.
  const lastFiredAtRef = useRef<number | null>(null);
  const hasFiredOnceRef = useRef(false);
  const isFetching = isLoading || isRefetching;
  const wasFetching = usePrevious(isFetching);

  useEffect(() => {
    // Bail until we observe a settled fetch (true -> false transition).
    if (!wasFetching || isFetching) {
      return;
    }

    // The fetch settled; consume the user-initiated flag now so it cannot
    // leak into a later auto-refresh if we skip the event below.
    const wasUserInitiated = userInitiatedRefreshRef.current;
    userInitiatedRefreshRef.current = false;

    if (isViewingOtherWallet || !isConnected || error != null || walletCount === 0) {
      return;
    }

    const now = Date.now();
    // `Math.max` guards against negative deltas from clock skew (NTP/battery).
    const refreshIntervalSeconds =
      lastFiredAtRef.current == null
        ? 0
        : Math.max(0, Math.round((now - lastFiredAtRef.current) / 1000));

    let refreshTrigger: PortfolioDataRefreshedProperties['refresh_trigger'];
    if (wasUserInitiated) {
      refreshTrigger = 'user_initiated';
    } else if (!hasFiredOnceRef.current) {
      const [navEntry] = performance.getEntriesByType(
        'navigation',
      ) as PerformanceNavigationTiming[];
      refreshTrigger = navEntry?.type === 'reload' ? 'browser_refresh' : 'page_load';
    } else {
      refreshTrigger = 'auto_refresh';
    }

    ampli.portfolioDataRefreshed({
      refresh_trigger: refreshTrigger,
      refresh_interval_seconds: refreshIntervalSeconds,
      portfolio_asset_count: allTokens.length,
      portfolio_total_value_usd: totalValueUsd,
      wallet_count: walletCount,
      chain_count: chainCount,
    });

    lastFiredAtRef.current = now;
    hasFiredOnceRef.current = true;
  }, [
    isFetching,
    wasFetching,
    isViewingOtherWallet,
    isConnected,
    error,
    walletCount,
    chainCount,
    allTokens.length,
    totalValueUsd,
  ]);

  const { pnl, isLoading: isPnLLoading } = useWalletPnL({
    evmAddress: effectiveEvmAddress,
    solanaAddress: effectiveSolAddress,
    enabled,
    chainId: selectedChain,
  });

  const { data: portfolio, isLoading: isPortfolioLoading } = useWalletPortfolio({
    evmAddress: effectiveEvmAddress,
    solanaAddress: effectiveSolAddress,
    enabled,
  });

  const { data: unfilteredPortfolio } = useWalletPortfolio({
    evmAddress,
    solanaAddress: solAddress,
    enabled,
  });

  const walletTotals = useMemo<Record<WalletFilter, number> | undefined>(() => {
    if (!unfilteredPortfolio) return undefined;
    const solana = unfilteredPortfolio.chainDistribution[CHAIN_IDS.SOLANA] ?? 0;
    return {
      all: unfilteredPortfolio.totalValue,
      evm: unfilteredPortfolio.totalValue - solana,
      solana,
    };
  }, [unfilteredPortfolio]);

  const { chart: portfolioChart, isLoading: isChartLoading } = useWalletChart({
    evmAddress: effectiveEvmAddress,
    solanaAddress: effectiveSolAddress,
    period: chartPeriod,
    enabled,
    chainId: selectedChain,
  });

  const pnlTokenBlocks = useMemo(() => buildPnLTokenBlocks(allTokens), [allTokens]);

  const { tokensPnL } = useTokensPnL({
    evmAddress: effectiveEvmAddress,
    solanaAddress: effectiveSolAddress,
    tokenBlocks: pnlTokenBlocks,
    enabled: enabled && pnlTokenBlocks.length > 0,
    fetchAll:
      sortColumn === 'unrealizedPnl' ||
      sortColumn === 'unrealizedReturn' ||
      showSmallBalances ||
      selectedChain !== 'all' ||
      searchQuery.trim().length > 0,
  });

  const getPnLForToken = useCallback(
    (token: TokenBalance): TokenPnL | undefined => {
      const key = `${token.chainId}:${token.tokenAddress}`;
      return tokensPnL.get(key);
    },
    [tokensPnL],
  );

  const filteredTokens = useMemo(() => {
    let tokens = allTokens;

    if (selectedChain !== 'all') {
      tokens = tokens.filter((token) => token.chainId === selectedChain);
    }

    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase().trim();
      tokens = tokens.filter((token) => {
        const nameMatch = token.name?.toLowerCase().includes(query);
        const symbolMatch = token.symbol?.toLowerCase().includes(query);
        const addressMatch = token.tokenAddress.toLowerCase() === query;
        return nameMatch || symbolMatch || addressMatch;
      });
    }

    return tokens;
  }, [allTokens, selectedChain, searchQuery]);

  const filteredTotalValue = useMemo(() => {
    if (selectedChain === 'all') return totalValueUsd;
    return filteredTokens.reduce((sum, token) => sum + (token.balanceUsd ?? 0), 0);
  }, [filteredTokens, selectedChain, totalValueUsd]);

  const { tokensWithPrice, tokensWithoutPrice } = useMemo(() => {
    const withPrice: TokenBalance[] = [];
    const withoutPrice: TokenBalance[] = [];

    for (const token of filteredTokens) {
      if (token.balanceUsd != null && token.balanceUsd >= 0) {
        withPrice.push(token);
      } else {
        withoutPrice.push(token);
      }
    }

    return { tokensWithPrice: withPrice, tokensWithoutPrice: withoutPrice };
  }, [filteredTokens]);

  const smallBalanceThreshold = useMemo(() => {
    if (filteredTotalValue <= 0) return 0;
    return filteredTotalValue * 0.01;
  }, [filteredTotalValue]);

  const groupedTokensWithPrice = useMemo(() => {
    if (selectedChain !== 'all') return null;
    return groupTokensByFungible(tokensWithPrice);
  }, [tokensWithPrice, selectedChain]);

  const MIN_VISIBLE_TOKENS = 10;

  const { largeBalanceTokens, smallBalanceTokens, smallBalancesTotal } = useMemo(() => {
    if (groupedTokensWithPrice) {
      const large: (TokenBalance | GroupedTokenBalance)[] = [];
      const small: (TokenBalance | GroupedTokenBalance)[] = [];
      let smallTotal = 0;

      for (let i = 0; i < groupedTokensWithPrice.length; i++) {
        const item = groupedTokensWithPrice[i];
        const value = isGroupedToken(item) ? item.totalBalanceUsd : (item.balanceUsd ?? 0);
        if (i < MIN_VISIBLE_TOKENS || value >= smallBalanceThreshold) {
          large.push(item);
        } else {
          small.push(item);
          smallTotal += value;
        }
      }

      return {
        largeBalanceTokens: large,
        smallBalanceTokens: small,
        smallBalancesTotal: smallTotal,
      };
    }

    const large: TokenBalance[] = [];
    const small: TokenBalance[] = [];
    let smallTotal = 0;

    for (let i = 0; i < tokensWithPrice.length; i++) {
      const token = tokensWithPrice[i];
      const value = token.balanceUsd ?? 0;
      if (i < MIN_VISIBLE_TOKENS || value >= smallBalanceThreshold) {
        large.push(token);
      } else {
        small.push(token);
        smallTotal += value;
      }
    }

    return { largeBalanceTokens: large, smallBalanceTokens: small, smallBalancesTotal: smallTotal };
  }, [tokensWithPrice, smallBalanceThreshold, groupedTokensWithPrice]);

  const getSortValue = useCallback(
    (item: TokenBalance | GroupedTokenBalance, column: SortColumn): number => {
      if (column === 'value') {
        return isGroupedToken(item) ? item.totalBalanceUsd : (item.balanceUsd ?? 0);
      }
      if (column === 'allocation') {
        const value = isGroupedToken(item) ? item.totalBalanceUsd : (item.balanceUsd ?? 0);
        return filteredTotalValue > 0 ? (value / filteredTotalValue) * 100 : 0;
      }
      if (column === 'unrealizedReturn') {
        if (isGroupedToken(item)) {
          let totalUnrealizedGain = 0;
          let totalCostBasis = 0;
          for (const position of item.positions) {
            const pnl = getPnLForToken(position);
            if (pnl) {
              totalUnrealizedGain += pnl.unrealizedGain;
              totalCostBasis += (position.balanceUsd ?? 0) - pnl.unrealizedGain;
            }
          }
          return totalCostBasis > 0 ? (totalUnrealizedGain / totalCostBasis) * 100 : 0;
        }
        const pnl = getPnLForToken(item);
        if (!pnl) return 0;
        const costBasis = (item.balanceUsd ?? 0) - pnl.unrealizedGain;
        return costBasis > 0 ? (pnl.unrealizedGain / costBasis) * 100 : 0;
      }
      if (column === 'unrealizedPnl') {
        if (isGroupedToken(item)) {
          let totalUnrealizedGain = 0;
          for (const position of item.positions) {
            const pnl = getPnLForToken(position);
            if (pnl) totalUnrealizedGain += pnl.unrealizedGain;
          }
          return totalUnrealizedGain;
        }
        const pnl = getPnLForToken(item);
        return pnl?.unrealizedGain ?? 0;
      }
      return 0;
    },
    [filteredTotalValue, getPnLForToken],
  );

  const sortTokens = useCallback(
    <T extends TokenBalance | GroupedTokenBalance>(tokens: T[]): T[] => {
      return [...tokens].sort((a, b) => {
        const aValue = getSortValue(a, sortColumn);
        const bValue = getSortValue(b, sortColumn);
        const diff = aValue - bValue;
        return sortDirection === 'desc' ? -diff : diff;
      });
    },
    [getSortValue, sortColumn, sortDirection],
  );

  const sortedTokensWithPrice = useMemo(() => {
    if (showSmallBalances) {
      return sortTokens([...largeBalanceTokens, ...smallBalanceTokens]);
    }
    return sortTokens(largeBalanceTokens);
  }, [largeBalanceTokens, smallBalanceTokens, showSmallBalances, sortTokens]);

  const portfolioRows = useMemo(() => {
    return buildPortfolioRows({
      tokens: sortedTokensWithPrice,
      getPnLForToken,
    });
  }, [sortedTokensWithPrice, getPnLForToken]);

  const unpricedRows = useMemo(() => {
    return buildPortfolioRows({
      tokens: tokensWithoutPrice,
      getPnLForToken,
    });
  }, [tokensWithoutPrice, getPnLForToken]);

  const sorting: SortingState = useMemo(
    () => [{ id: sortColumn, desc: sortDirection === 'desc' }],
    [sortColumn, sortDirection],
  );

  const handleSortingChange = useCallback(
    (updaterOrValue: SortingState | ((prev: SortingState) => SortingState)) => {
      const newSorting =
        typeof updaterOrValue === 'function' ? updaterOrValue(sorting) : updaterOrValue;
      if (newSorting.length > 0) {
        const { id, desc } = newSorting[0];
        if (isSortColumn(id)) setSortColumn(id);
        setSortDirection(desc ? 'desc' : 'asc');
      }
    },
    [sorting],
  );

  const portfolioMeta: PortfolioTableMeta = useMemo(
    () => ({
      totalPortfolioValue: filteredTotalValue,
      formatFiat,
      isWatchOnly: isViewingOtherWallet,
      onAddTriggerOrders: setTriggerOrderTarget,
    }),
    [filteredTotalValue, formatFiat, isViewingOtherWallet],
  );

  const table = useReactTable<PortfolioRow>({
    data: portfolioRows,
    columns: portfolioColumns,
    getCoreRowModel: getCoreRowModel(),
    getExpandedRowModel: getExpandedRowModel(),
    manualSorting: true,
    getSubRows: (row) => row.subRows,
    state: { sorting, expanded },
    onSortingChange: handleSortingChange,
    onExpandedChange: setExpanded,
    meta: { portfolioMeta },
  });

  const chainsWithTokens = useMemo<ChainFilterOption[]>(() => {
    const balancesByChain = new Map<number, number>();

    for (const token of allTokens) {
      const current = balancesByChain.get(token.chainId) ?? 0;
      const balanceUsd = token.balanceUsd != null && token.balanceUsd > 0 ? token.balanceUsd : 0;
      balancesByChain.set(token.chainId, current + balanceUsd);
    }

    const chains: ChainFilterOption[] = Array.from(balancesByChain, ([id, balanceUsd]) => ({
      id,
      name: NETWORK_NAME_PER_CHAIN_ID[id] ?? `Chain ${id}`,
      balanceUsd,
    })).sort((chainA, chainB) => chainB.balanceUsd - chainA.balanceUsd);

    return [{ id: 'all', name: 'All networks', balanceUsd: totalValueUsd }, ...chains];
  }, [allTokens, totalValueUsd]);

  useEffect(() => {
    if (
      !isLoading &&
      selectedChain !== 'all' &&
      !chainsWithTokens.some(({ id }) => id === selectedChain)
    ) {
      setSelectedChain('all');
    }
  }, [chainsWithTokens, isLoading, selectedChain]);

  const { feedbackModalOpen, setFeedbackModalOpen, handleFeedbackModalClose } =
    usePortfolioFeedbackPrompt({ enabled: !isViewingOtherWallet && isConnected });

  if (!enabled) {
    return (
      <div className="bg-special-background-app bg-(image:var(--gradient-special-homepage)) min-h-screen">
        <div className="h-[62px] [body[data-show-banner='true']_&]:h-[102px]" />

        <div className="py-4 px-4">
          <h1
            className={fontStyles({
              variant: 'H2',
              class: ['max-w-[1240px]', 'mx-auto', 'my-6', 'text-left'],
            })}
          >
            Portfolio
          </h1>

          <div
            className={classNames(
              containerClass,
              'flex items-center justify-center w-full h-[75vh]',
            )}
          >
            <div className="h-fit text-center">
              <span
                className={fontStyles({
                  variant: 'LABEL_LG',
                  class: ['flex justify-center mb-2'],
                })}
              >
                Your portfolio will appear here.
              </span>
              <div
                className={fontStyles({
                  variant: 'BODY_MD',
                  class: ['flex justify-center mt-2 mb-4 text-token-gray-700'],
                })}
              >
                Connect wallet to see your token balances.
              </div>
              <span className="flex justify-center">
                <HeaderConnectButton />
              </span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-special-background-app bg-(image:var(--gradient-special-homepage)) min-h-screen">
      <div className="h-[62px] [body[data-show-banner='true']_&]:h-[102px]" />

      <div className="py-4 tablet:pb-0 tablet:pt-8 mb-3 tablet:mb-13">
        <PortfolioHeader
          totalValue={filteredTotalValue}
          isLoading={isLoading}
          pnl={pnl}
          isPnLLoading={isPnLLoading}
          portfolio={portfolio}
          isPortfolioLoading={isPortfolioLoading}
          chart={portfolioChart}
          isChartLoading={isChartLoading}
          chartPeriod={chartPeriod}
          onChartPeriodChange={setChartPeriod}
          selectedChain={selectedChain}
          onChainChange={setSelectedChain}
          chainsWithTokens={chainsWithTokens}
          selectedWallet={selectedWallet}
          onWalletChange={setSelectedWallet}
          walletOptions={walletOptions}
          walletTotals={walletTotals}
          isWatchOnly={isViewingOtherWallet}
          tokens={filteredTokens}
          isRefreshing={isRefetching}
          onRefresh={handleRefresh}
        />
        <div className="px-4">
          <div className={containerClass}>
            <div className="min-h-[280px] tablet:min-h-[400px]">
              <PortfolioTable
                table={table}
                isLoading={isLoading}
                error={error}
                onRetry={handleRefresh}
                hasTokens={allTokens.length > 0}
                hasFilteredTokens={filteredTokens.length > 0}
                selectedChain={selectedChain}
                searchQuery={searchQuery}
                onSearchQueryChange={setSearchQuery}
                unpricedRows={unpricedRows}
                unpricedCount={tokensWithoutPrice.length}
                showUnpricedTokens={showUnpricedTokens}
                onShowUnpricedTokensChange={setShowUnpricedTokens}
                smallBalanceCount={smallBalanceTokens.length}
                smallBalancesTotal={smallBalancesTotal}
                showSmallBalances={showSmallBalances}
                onShowSmallBalancesChange={setShowSmallBalances}
              />
            </div>
          </div>
        </div>
      </div>
      <FeedbackModal
        isOpen={feedbackModalOpen}
        onOpenChange={setFeedbackModalOpen}
        onClose={handleFeedbackModalClose}
        triggerSource="portfolio"
      />
      <PortfolioTriggerOrdersModal
        target={triggerOrderTarget}
        onClose={() => setTriggerOrderTarget(null)}
      />
    </div>
  );
}
